// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedicationRequest, Practitioner, Provenance, Reference } from '@medplum/fhirtypes';

const FHIR_ACT_REASON_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-ActReason';
const FHIR_PROVENANCE_PARTICIPANT_TYPE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/provenance-participant-type';
const FHIR_DOCUMENT_COMPLETION_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-DocumentCompletion';

export function getStoredOrderSigner(order: MedicationRequest | undefined): Reference<Practitioner> | undefined {
  return order?.requester as Reference<Practitioner> | undefined;
}

export function getOrderSignerForSave(
  existingOrder: MedicationRequest | undefined,
  currentProvider: Practitioner,
  editing: boolean
): Reference<Practitioner> {
  if (!currentProvider.id) {
    throw new Error('Cannot sign RPM order without a provider id.');
  }
  if (editing && existingOrder?.requester) {
    return existingOrder.requester as Reference<Practitioner>;
  }
  return {
    reference: `Practitioner/${currentProvider.id}`,
    display: currentProvider.name?.[0]
      ? [currentProvider.name[0].given?.join(' '), currentProvider.name[0].family].filter(Boolean).join(' ')
      : undefined,
  };
}

/**
 * Builds the signature Provenance for an order (RPM, referral, …). `orderReference` may be a `urn:uuid:` fullUrl so the
 * Provenance can be written in the same transaction as the order it signs.
 */
export function buildOrderSignatureProvenance(
  orderReference: string,
  signer: Reference<Practitioner>,
  signedAt: string
): Provenance {
  return {
    resourceType: 'Provenance',
    target: [{ reference: orderReference }],
    recorded: signedAt,
    reason: [{ coding: [{ system: FHIR_ACT_REASON_SYSTEM, code: 'SIGN', display: 'Signed' }] }],
    agent: [
      {
        type: { coding: [{ system: FHIR_PROVENANCE_PARTICIPANT_TYPE_SYSTEM, code: 'author' }] },
        who: signer,
      },
    ],
    signature: [
      {
        type: [{ system: FHIR_DOCUMENT_COMPLETION_SYSTEM, code: 'LA', display: 'legally authenticated' }],
        when: signedAt,
        who: signer,
      },
    ],
  };
}

export const buildRpmOrderSignatureProvenance = buildOrderSignatureProvenance;
